import {
    Alert,
    AlertTitle,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import { Alert as AntAlert } from 'antd';
import { useTranslation } from 'react-i18next';
import { CASH_BOOKING_REFUSAL_TITLE_KEY } from '../domain/cashBookingRefusal';

type RefusalProps = Readonly<{ refusalKey: string | null }>;

/** The page-top warning of a page whose form books money, while this station cannot book it. */
export function CashBookingRefusalBanner({ refusalKey }: RefusalProps) {
    const { t } = useTranslation();
    if (!refusalKey) {
        return null;
    }
    return (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="cash-booking-refusal">
            <AlertTitle>{t(CASH_BOOKING_REFUSAL_TITLE_KEY)}</AlertTitle>
            {t(refusalKey)}
        </Alert>
    );
}

/** The same warning for the Ant Design pages. */
export function CashBookingRefusalNotice({ refusalKey }: RefusalProps) {
    const { t } = useTranslation();
    if (!refusalKey) {
        return null;
    }
    return (
        <AntAlert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            data-testid="cash-booking-refusal"
            message={t(CASH_BOOKING_REFUSAL_TITLE_KEY)}
            description={t(refusalKey)}
        />
    );
}

/** Says why a button that starts a booking did not open its form. */
export function CashBookingRefusalDialog({
    refusalKey,
    onClose,
}: Readonly<{ refusalKey: string | null; onClose: () => void }>) {
    const { t } = useTranslation();
    return (
        <Dialog
            open={refusalKey !== null}
            onClose={onClose}
            aria-labelledby="cash-booking-refusal-title"
            data-testid="cash-booking-refusal-dialog"
        >
            <DialogTitle id="cash-booking-refusal-title">
                {t(CASH_BOOKING_REFUSAL_TITLE_KEY)}
            </DialogTitle>
            <DialogContent>
                <Alert severity="warning">{refusalKey ? t(refusalKey) : ''}</Alert>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    {t('common.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
